import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Button } from './button'
import { Bold, Check, Italic, List, Link as LinkIcon, Unlink, X } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ImproveWithAiButton, hasImprovableText, type AiFieldKind } from './improve-with-ai'

/** An http(s) address from what was typed; a bare domain gets `https://`. Anything else is refused. */
const toHttpUrl = (raw: string): string | null => {
  const value = raw.trim()
  if (!value) return null
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`
  try {
    const url = new URL(withScheme)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

interface EditorProps {
  value?: string
  content?: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  /** F-105: what the field is, for "Improve with AI" (defaults to `other`). */
  aiFieldKind?: AiFieldKind
}

export const Editor = ({ value, content, onChange, className, aiFieldKind = 'other' }: EditorProps) => {
  const { t } = useTranslation('common')
  const initialContent = content || value || ''
  const [linking, setLinking] = React.useState(false)
  const [href, setHref] = React.useState('')
  const [empty, setEmpty] = React.useState(!hasImprovableText(initialContent, 'html'))

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-purple-600 hover:text-purple-700 underline',
        },
      }),
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      setEmpty(editor.isEmpty)
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none ${className || 'min-h-[200px]'}`,
      },
    },
  })

  React.useEffect(() => {
    if (editor && (content || value)) {
      const newContent = content || value || ''
      if (editor.getHTML() !== newContent) {
        editor.commands.setContent(newContent)
      }
      setEmpty(editor.isEmpty)
    }
  }, [content, value, editor])

  if (!editor) {
    return null
  }

  const cancelLink = () => { setLinking(false); setHref(''); editor.commands.focus() }
  const applyLink = () => {
    const url = toHttpUrl(href)
    if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    setLinking(false)
    setHref('')
  }

  return (
    <div className="border rounded-md">
      <div className="border-b p-2 flex flex-wrap gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-gray-100' : ''}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-gray-100' : ''}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-gray-100' : ''}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('editor.link')}
          title={t('editor.link')}
          onClick={() => { setHref(editor.getAttributes('link').href ?? ''); setLinking(true) }}
          className={editor.isActive('link') ? 'bg-gray-100' : ''}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        {editor.isActive('link') && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('editor.unlink')}
            title={t('editor.unlink')}
            onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
          >
            <Unlink className="h-4 w-4" />
          </Button>
        )}
        <ImproveWithAiButton
          className="ml-auto"
          format="html"
          fieldKind={aiFieldKind}
          empty={empty}
          getText={() => editor.getHTML()}
          onApply={(html) => {
            editor.commands.setContent(html)
            setEmpty(editor.isEmpty)
            onChange(editor.getHTML())
          }}
        />
      </div>
      {linking && (
        <div className="flex items-center gap-1 border-b p-2">
          <input
            autoFocus
            value={href}
            onChange={(e) => setHref(e.target.value)}
            placeholder="https://"
            inputMode="url"
            data-guard-ignore
            aria-label={t('editor.linkUrl')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink() }
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelLink() }
            }}
            className="h-8 min-w-0 flex-1 rounded-md border border-gray-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <Button type="button" variant="ghost" size="sm" aria-label={t('editor.applyLink')} title={t('editor.applyLink')} onClick={applyLink}>
            <Check className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="sm" aria-label={t('cancel')} title={t('cancel')} onClick={cancelLink}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <EditorContent editor={editor} className="p-4" />
    </div>
  )
}